import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Row, Col, message, Tooltip, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, InfoCircleOutlined, CloseCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import dayjs from 'dayjs';

const EventDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [participantsCancelled, setParticipantsCancelled] = useState([]);
  const [participantsRegistered, setParticipantsRegistered] = useState([]);
  const [participantsUnpaid, setParticipantsUnpaid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [programmePoints, setProgrammePoints] = useState([]);
  const [presentCounts, setPresentCounts] = useState({});
  const [participantsTotal, setParticipantsTotal] = useState([]);
  
  const { event, eventsList } = location.state || {};

  const currentIndex = eventsList.findIndex(e => e.eventId === event.eventId);
  const prevEvent = currentIndex > 0 ? eventsList[currentIndex - 1] : null;
  const nextEvent = currentIndex < eventsList.length - 1 ? eventsList[currentIndex + 1] : null;

  const goToEvent = (event) => {
    navigate(`/event-details`, {
      state: { event, eventsList },
    });
  };

    useEffect(() => {
        const init = async () => {
            if (event && event.host_url) {
                setLoading(true);

                try {
                    const tokenRes = await axios.get(`/regasus/token?host=${event.host_url}`);
                    const token = tokenRes.data?.access_token;
                    if (!token) return message.error('Token not retrieved');

                    await fetchParticipantsData(event.eventId, event.host_url, token);
                    await fetchProgrammePointsAndCounts(event.eventId, event.host_url, token);
                } catch (err) {
                    console.error('Error during event data loading', err);
                    message.error('Error initializing event data');
                } finally {
                    setLoading(false);
                }
            }
        };

        init();
    }, [event]);

  if (!event || !eventsList) {
    return <p>Error: No event details or events list found</p>;
  }

  const fetchParticipantsData = async (eventId, host) => {
    try {
        const tokenRes = await axios.get(`/regasus/token?host=${host}`);
        const token = tokenRes.data?.access_token;
        if (!token) return message.error('Token not retrieved');

        const baseUrl = `/regasus/participants/count?host=${host}&token=${encodeURIComponent(token)}`;

        // Count all participants (no filters)
        const totalCountRes = await axios.post(baseUrl, {
            eventPK: eventId
        });
        setParticipantsTotal(Array.from({ length: totalCountRes.data.count }));

        // Count cancelled (StateID 3 and 4?)
        const cancelledCountRes = await axios.post(baseUrl, {
            eventPK: eventId,
            participantStatePK: [3]
        });
        setParticipantsCancelled(Array.from({ length: cancelledCountRes.data.count }));

        // Count registered (hasRegistration = 1 AND participantStatePK: 2)
        const registeredCountRes = await axios.post(baseUrl, {
            eventPK: eventId,
            hasRegistration: '1',
            participantStatePK: 2
        });
        setParticipantsRegistered(Array.from({ length: registeredCountRes.data.count }));

        // Count unpaid (participantStatePK: 101245512266642)
        const unpaidCountRes = await axios.post(baseUrl, {
            eventPK: eventId,
            participantStatePK: 101245512266642
        });
        setParticipantsUnpaid(Array.from({ length: unpaidCountRes.data.count }));

    } catch (err) {
        console.error(err);
        message.error('Error fetching participant counts');
    }
  };

  const fetchProgrammePointsAndCounts = async (eventId, host, token) => {
    try {
        const resPPs = await axios.get(`/regasus/event-pps/${eventId}?host=${host}&token=${encodeURIComponent(token)}`);
        const rawPoints = resPPs.data?.programmePoints?.programmePoint;

        if (!Array.isArray(rawPoints)) {
            console.warn('Unexpected response format for programmePoints:', resPPs.data);
            return setProgrammePoints([]);
        }

        // Mapping with ID and name (in or from)
        const points = rawPoints.map((pp) => {
            const nameEntry =
                pp.name?.find(n => n.lang === 'en') ||
                pp.name?.find(n => n.lang === 'de') ||
                { value: `PP ${pp.id}` };

            return {
                programmePointId: pp.id,
                name: nameEntry.value
            };
        });

        const counts = {};

        await Promise.all(
            points.map(async (pp) => {
                try {
                    const resCount = await axios.get(
                        `/regasus/programme-point/${pp.programmePointId}/present-count?host=${host}&token=${encodeURIComponent(token)}`
                    );
                    counts[pp.programmePointId] = parseInt(resCount.data.count, 10);
                } catch (err) {
                    console.warn(`Error getting count for PP ${pp.programmePointId}`, err.message);
                    counts[pp.programmePointId] = 0;
                }
            })
        );

        // Filter only those with count > 0
        const filteredPoints = points.filter(pp => counts[pp.programmePointId] > 0);

        setProgrammePoints(filteredPoints);
        setPresentCounts(counts);

        return { points: filteredPoints, counts };
    } catch (err) {
        console.error('Error fetching programme points or counts:', err);
        message.error('Error loading programme points or present counts');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Spin spinning={loading}>
        <h1 style={{ marginBottom: '40px', display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
            <ArrowLeftOutlined
              style={{
                marginRight: '16px',
                cursor: prevEvent ? 'pointer' : 'not-allowed',
                color: prevEvent ? 'blue' : 'grey'
              }}
              onClick={() => prevEvent && goToEvent(prevEvent)}
            />
            {event.mnemonic[0]?.value || event.mnemonic}
            <ArrowRightOutlined
              style={{
                marginLeft: '16px',
                cursor: nextEvent ? 'pointer' : 'not-allowed',
                color: nextEvent ? 'blue' : 'grey'
              }}
              onClick={() => nextEvent && goToEvent(nextEvent)}
            />
        </h1>

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
                <Card
                    title={
                        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                            <InfoCircleOutlined style={{ marginRight: 8 }} />
                            Total Participants
                        </span>
                    }
                >
                    <p style={{ fontSize: '30px', color: '#1890ff', textAlign: 'center', margin: 0 }}>
                        {participantsTotal.length}
                    </p>
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                  <Card
                      title={
                          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                          <CheckCircleOutlined style={{ marginRight: 8 }} />
                          With Registration
                        </span>
                      }
                  >
                      <p style={{ fontSize: '30px', color: '#52c41a', textAlign: 'center', margin: 0 }}>
                          {participantsRegistered.length}
                      </p>
                  </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card
                    title={
                        <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
                          <CloseCircleOutlined style={{ marginRight: 8 }} />
                          Cancelled Participants
                        </span>
                    }
                >
                    <p style={{ fontSize: '30px', color: '#f5222d', textAlign: 'center', margin: 0 }}>
                        {participantsCancelled.length}
                    </p>
                </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card
                  title={
                      <span style={{ color: '#faad14', fontWeight: 'bold' }}>
                        <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                        Unpaid Participants
                      </span>
                  }
              >
                  <p style={{ fontSize: '30px', color: '#faad14', textAlign: 'center', margin: 0 }}>
                      {participantsUnpaid.length}
                  </p>
              </Card>
            </Col>
        </Row>

        <Row gutter={[16, 16]}>
            {/* Event details card */}
            <Col xs={24} sm={12} md={12} lg={12}>
                <Card
                    title={
                        <Tooltip color="#1B8AF1" title={`Event ID: ${event.eventId}`}>
                            <div style={{ color: '#1B8AF1' }}>
                                <InfoCircleOutlined style={{ marginRight: '10px', color: '#1B8AF1' }} />
                                Event Details
                            </div>
                        </Tooltip>
                    }
                    hoverable={true}>
                    <p><strong>Name:</strong> {event.name}</p>
                    <p><strong>Mnemonic:</strong> {event.mnemonic}</p>
                    <p><strong>City:</strong> {event.city}</p>
                    <p><strong>Begin Date:</strong> {dayjs(event.beginDate).format('DD-MM-YYYY')}</p>
                    <p><strong>End Date:</strong> {dayjs(event.endDate).format('DD-MM-YYYY')}</p>
                </Card>
            </Col>

            {/* Table of participants present by PP */}
            <Col xs={24} sm={12} md={12} lg={12}>
                <Card
                    title={
                        <Tooltip color="#1B8AF1" title={`Event ID: ${event.eventId}`}>
                            <div style={{ color: '#1B8AF1' }}>
                                <InfoCircleOutlined style={{ marginRight: '10px', color: '#1B8AF1' }} />
                                Present at Programme Points
                            </div>
                        </Tooltip>
                    }
                    hoverable
                >
                    {programmePoints.length === 0 ? (
                        <p>No programme points found.</p>
                    ) : (
                        <table style={{ width: '100%' }}>
                            <thead>
                            <tr>
                                <th style={{ textAlign: 'left', paddingBottom: 8 }}>Name</th>
                                <th style={{ textAlign: 'right', paddingBottom: 8 }}>Present</th>
                            </tr>
                            </thead>
                            <tbody>
                            {programmePoints.map(pp => (
                                <tr key={pp.programmePointId}>
                                    <td style={{ padding: '4px 0' }}>{pp.name}</td>
                                    <td style={{ padding: '4px 0', textAlign: 'right' }}>
                                        {presentCounts[pp.programmePointId] ?? '...'}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </Card>
            </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default EventDetails;
